import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Chip,
  IconButton
} from '@mui/material';
import {
  CloudUpload,
  CheckCircle,
  Error as ErrorIcon,
  Description,
  Close
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import { apiClient } from '@/services/api';

interface IndicatorImportDialogProps {
  open: boolean;
  onClose: () => void;
  projectId: string;
  onImportSuccess: (importedIndicators: any[]) => void;
}

const IndicatorImportDialog: React.FC<IndicatorImportDialogProps> = ({ 
  open, 
  onClose, 
  projectId, 
  onImportSuccess 
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [importResult, setImportResult] = useState<any>(null);

  // 文件拖拽处理
  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      const selectedFile = acceptedFiles[0];
      const fileName = selectedFile.name.toLowerCase();
      
      if (!fileName.endsWith('.xlsx') && !fileName.endsWith('.xls') && !fileName.endsWith('.docx')) {
        return;
      }
      
      setFile(selectedFile);
      setImportResult(null);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx']
    },
    multiple: false
  });

  // 处理文件上传和导入
  const handleImport = async () => {
    if (!file) {
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append('file', file);

      // 模拟上传进度
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 200);

      // 根据文件类型选择不同的API端点
      const lowerCaseFileName = file.name.toLowerCase();
      const isWord = lowerCaseFileName.endsWith('.docx');
      const apiEndpoint = isWord
        ? `/projects/${projectId}/indicators/import-word`
        : `/projects/${projectId}/indicators/import-excel`;

      const response = await apiClient.post(apiEndpoint, formData);

      clearInterval(progressInterval);
      setUploadProgress(100);

      if (response.success) {
        setImportResult(response);
        onImportSuccess(response.data?.importedIndicators || []);
        setTimeout(() => {
          onClose();
          setFile(null);
          setImportResult(null);
          setUploadProgress(0);
        }, 2000);
      } else {
        throw new Error(response.message || '导入失败');
      }
    } catch (error: any) {
      setImportResult({
        success: false,
        error: error.message || '导入失败'
      });
    } finally {
      setIsUploading(false);
    }
  };

  // 重置状态
  const handleClose = () => {
    setFile(null);
    setImportResult(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">导入指标</Typography>
          <IconButton onClick={handleClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ color: 'error.main', fontWeight: 600, whiteSpace: 'pre-line' }}>
          {`本导入功能将根据您上传文件中的三级指标名称进行智能更新：
　　  已有指标：若系统中存在同名三级指标，将自动更新该指标的其他信息（如权重分值、评价规则等）。
　　  新增指标：若系统中不存在同名三级指标，将自动追加为新指标。
请确保您的导入文件中"三级指标"名称准确无误，这将作为匹配和更新的唯一依据。`}
          </Typography>
        </Box>
        {!file && (
          <Box
            {...getRootProps()}
            sx={{
              border: '2px dashed',
              borderColor: isDragActive ? 'primary.main' : 'grey.300',
              borderRadius: 2,
              p: 4,
              textAlign: 'center',
              cursor: 'pointer',
              backgroundColor: isDragActive ? 'action.hover' : 'background.paper',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                borderColor: 'primary.main',
                backgroundColor: 'action.hover'
              }
            }}
          >
            <input {...getInputProps()} />
            <CloudUpload sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              {isDragActive ? '松开文件以上传' : '拖拽文件到此处或点击选择文件'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              支持 .xlsx, .xls, .docx 格式
            </Typography>
          </Box>
        )}

        {file && !importResult && (
          <Box sx={{ mt: 2 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
              <Box display="flex" alignItems="center">
                <Description sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="body1" fontWeight="medium">
                  {file.name}
                </Typography>
              </Box>
              <Chip
                label={file.name.toLowerCase().endsWith('.docx') ? 'Word文档' : 'Excel文档'}
                color="primary"
                size="small"
              />
            </Box>
            
            {isUploading && (
              <Box>
                <Typography variant="body2" gutterBottom>
                  正在导入文件...
                </Typography>
                <LinearProgress variant="determinate" value={uploadProgress} />
                <Typography variant="caption" color="text.secondary">
                  {uploadProgress}%
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {importResult && (
          <Box>
            {importResult.success ? (
              <Alert severity="success" sx={{ mb: 2 }}>
                导入成功！共导入 {importResult.data?.importedCount || 0} 个指标
              </Alert>
            ) : (
              <Alert severity="error" sx={{ mb: 2 }}>
                导入失败：{importResult.error}
              </Alert>
            )}

            {/* 导入详情 */}
            {importResult.data && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  导入详情
                </Typography>

                {/* 成功导入的指标 */}
                {importResult.data.importedIndicators && importResult.data.importedIndicators.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      成功导入的指标 ({importResult.data.importedIndicators.length})
                    </Typography>
                    <List dense>
                      {importResult.data.importedIndicators.map((indicator: any, index: number) => (
                        <ListItem key={index} sx={{ pl: 0 }}>
                          <CheckCircle sx={{ color: 'success.main', mr: 1, fontSize: 20 }} />
                          <ListItemText
                            primary={`${indicator.indicator_code || ''} ${indicator.indicator_name || ''}`}
                            secondary={`${indicator.level1_name || ''} - ${indicator.level2_name || ''}`}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}

                {/* 导入错误 */}
                {importResult.data.errors && importResult.data.errors.length > 0 && (
                  <Box>
                    <Typography variant="subtitle1" gutterBottom>
                      导入错误 ({importResult.data.errors.length})
                    </Typography>
                    <List dense>
                      {importResult.data.errors.map((error: any, index: number) => (
                        <ListItem key={index} sx={{ pl: 0 }}>
                          <ErrorIcon sx={{ color: 'error.main', mr: 1, fontSize: 20 }} />
                          <ListItemText
                            primary={error.message || '未知错误'}
                            secondary={error.details || ''}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>
          {importResult ? '关闭' : '取消'}
        </Button>
        {!importResult && file && (
          <Button
            onClick={handleImport}
            variant="contained"
            disabled={isUploading}
            startIcon={<CloudUpload />}
          >
            {isUploading ? '导入中...' : '开始导入'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default IndicatorImportDialog;
